import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useOnboardingStore } from '../../store/useOnboardingStore';

export const AuthCallback = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const { openModal } = useOnboardingStore();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // First check if we already have a session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          throw sessionError;
        }

        // If no session exists, exchange the code
        if (!session) {
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(window.location.href);
          
          if (exchangeError) {
            throw exchangeError;
          }

          if (!data.session) {
            throw new Error('No session established');
          }
        }

        // Get current user
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (userError || !user) {
          throw new Error('Failed to get user details');
        }

        // Get user profile from database
        const { data: profile, error: profileError } = await supabase
          .from('users')
          .select('*')
          .eq('id', user.id)
          .single();

        if (profileError) {
          console.error('Profile fetch error:', profileError);
        }

        // Check if user needs to complete onboarding
        const needsOnboarding = !profile || !profile.first_name || !profile.last_name;

        // Always navigate to dashboard, but open onboarding modal if needed
        navigate('/dashboard', { replace: true });
        if (needsOnboarding) {
          openModal();
        }

      } catch (err) {
        console.error('Auth callback error:', err);
        setError(err.message);
        // Redirect to home page after a short delay if there's an error
        setTimeout(() => navigate('/', { replace: true }), 3000);
      }
    };

    handleCallback();
  }, [navigate, openModal]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <div className="text-red-600 mb-4">⚠️</div>
          <p className="text-red-600 mb-2">Authentication failed</p>
          <p className="text-gray-500 text-sm">Redirecting to home page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-md text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
        <p className="text-gray-600">Completing sign in...</p>
        <p className="text-gray-400 text-sm mt-2">You'll be redirected automatically</p>
      </div>
    </div>
  );
};